/**
 * Cart UI — groups the Shopify cart lines into Design cards and draws each
 * design with the SAME renderer the builder uses (no stored images needed).
 */
import { loadData } from './yinn-wear-data.3fb8ca28.js';
import { groupDesigns } from './yinn-wear-cart-grouping.fe9222c0.js';
import { BraceletRenderer } from './yinn-wear-bracelet-renderer.bf82dcaa.js';
import { PlaqueRenderer } from './yinn-wear-plaque-renderer.1c2dfb98.js';
import { formatMoney } from './yinn-wear-format.6aec1b5d.js';
import { isEn } from './yinn-wear-i18n.787431a0.js';

const TYPE_NAMES = {
  bracelet: { en: 'Custom Bracelet', zh: '定制手串' },
  plaque: { en: 'Custom Plaque', zh: '定制香牌' },
  necklace: { en: 'Custom Necklace', zh: '定制项链' },
};

// TODO(bilingual): point this at the matching locale page once the customizer
// ships a zh-CN route. For now both languages land on the English customizer.
const CUSTOMIZER_PAGE = '/pages/design-your-scent-wear';

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

function propsOf(line) {
  const out = {};
  if (Array.isArray(line.properties)) for (const p of line.properties) out[p.name] = p.value;
  else if (line.properties) Object.assign(out, line.properties);
  return out;
}

function buildCard(design, data) {
  const en = isEn();
  const type = TYPE_NAMES[design.product_type] || { en: 'Custom Design', zh: '定制设计' };
  const label = (item) => (item ? (en && item.name_en ? item.name_en : item.name) : '');
  let meta;
  if (design.product_type === 'plaque' && data.plaque) {
    // Plaque: name · formula · cord · mounting (nothing else is user-chosen).
    meta = [
      label(data.plaque.plaqueById[design.plaque_id]),
      label(data.formulaById[design.formula_id]),
      label(data.plaque.cordById[design.plaque_cord_id]),
      label(data.plaque.mountingStyleById[design.mounting_style]),
    ]
      .filter(Boolean)
      .join(' · ');
  } else {
    const cord = data.cords.find((c) => c.component_id === design.cord_component_id);
    const cordName = cord ? (en ? cord.name_en || cord.name : cord.name) : design.cord_component_id || '';
    meta = [
      design.wrist_size_mm ? (en ? `Wrist ${design.wrist_size_mm} mm` : `腕围 ${design.wrist_size_mm} mm`) : '',
      design.sequence.length ? (en ? `${design.sequence.length} beads` : `${design.sequence.length} 颗`) : '',
      cordName ? (en ? `${cordName} cord` : `${cordName}绳`) : '',
    ]
      .filter(Boolean)
      .join(' · ');
  }

  const card = el('div', 'yinn-design-card');
  card.setAttribute('data-design-id', design.design_id);

  const preview = el('div', 'yinn-design-card__preview');
  card.appendChild(preview);

  const body = el('div', 'yinn-design-card__body');
  const head = el('div', 'yinn-design-card__head');
  head.appendChild(el('span', 'yinn-design-card__title', isEn() ? type.en : type.zh));
  head.appendChild(el('span', 'yinn-design-card__total', formatMoney(design.total_price, data.currencySymbol)));
  body.appendChild(head);
  if (meta) body.appendChild(el('p', 'yinn-design-card__meta', meta));

  const list = el('ul', 'yinn-design-card__lines');
  design.lines.forEach((line) => {
    const li = el('li');
    li.appendChild(el('span', null, line.variant_title || line.title));
    li.appendChild(
      el('span', null, `×${line.quantity} · ${formatMoney(line.final_line_price, data.currencySymbol)}`)
    );
    list.appendChild(li);
  });
  body.appendChild(list);

  const actions = el('div', 'yinn-design-card__actions');
  const edit = el('a', 'yinn-design-card__edit', isEn() ? 'Edit design' : '编辑设计');
  edit.href = `${CUSTOMIZER_PAGE}?design=${encodeURIComponent(design.design_id)}`;
  const remove = el('button', 'yinn-design-card__link', isEn() ? 'Remove' : '删除');
  remove.type = 'button';
  remove.setAttribute('data-remove-design', design.design_id);
  actions.append(edit, remove);
  body.appendChild(actions);
  card.appendChild(body);

  return { card, preview };
}

async function removeDesign(designId) {
  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const lines = (cart.items || []).filter((l) => propsOf(l)._design_id === designId);
  for (const line of lines) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 }),
    });
  }
  window.location.reload();
}

function onClick(e) {
  const rm = e.target.closest('[data-remove-design]');
  if (rm) {
    rm.disabled = true;
    removeDesign(rm.getAttribute('data-remove-design'));
  }
}

export async function initCartDesigns() {
  const table = document.querySelector('.cart-items');
  if (!table) return;

  let cart;
  try {
    cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  } catch {
    return;
  }

  const { designs } = groupDesigns(cart.items);
  if (!designs.length) return;

  const data = await loadData();

  const wrap = el('section', 'yinn-designs');
  wrap.appendChild(el('h2', 'yinn-designs__title', isEn() ? 'Your Designs' : '你的设计'));
  const previews = [];
  for (const design of designs) {
    const { card, preview } = buildCard(design, data);
    wrap.appendChild(card);
    previews.push({ preview, design });
  }

  const anchor = table.closest('.cart__items') || table;
  anchor.parentNode.insertBefore(wrap, anchor);

  // Hide the raw component rows (the design card represents them). Match each
  // line to its row by the line key the theme puts on the quantity input, and
  // fall back to row order if the theme markup ever changes.
  const designIds = new Set(designs.map((d) => d.design_id));
  const allRows = [...document.querySelectorAll('tr.cart-item')];
  const rowByKey = new Map();
  for (const row of allRows) {
    const key = row
      .querySelector('[data-quantity-line-key]')
      ?.getAttribute('data-quantity-line-key');
    if (key) rowByKey.set(key, row);
  }
  (cart.items || []).forEach((line, i) => {
    if (!designIds.has(propsOf(line)._design_id)) return;
    const row = rowByKey.get(line.key) || allRows[i];
    if (row) row.classList.add('yinn-design-line');
  });

  // Draw each design with the SAME renderer the builder uses (the renderer
  // measures once the preview is in the DOM), picked by product type.
  previews.forEach(({ preview, design }) => {
    if (design.product_type === 'plaque' && data.plaque) {
      const renderer = PlaqueRenderer({ data, el: preview });
      renderer.render({
        productType: 'plaque',
        plaqueId: design.plaque_id,
        formulaId: design.formula_id,
        plaqueCordId: design.plaque_cord_id,
        mountingStyle: design.mounting_style,
      });
      return;
    }
    const renderer = BraceletRenderer({ data, el: preview, motto: true });
    renderer.render({
      sequence: design.sequence,
      wristSizeMm: design.wrist_size_mm || 160,
      cordId: design.cord_component_id || 'CORD-BROWN',
    });
  });

  document.addEventListener('click', onClick);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDesigns);
} else {
  initCartDesigns();
}
